import type { AuthResponse, Article, ArticleInput, ArticlesResponse, User, UserInput, UserProfileInput, MediaItem, Category, CategoryInput } from './types.ts'

const API = '/api'

let csrfToken = ''

export function setCsrfToken(token: string) {
  csrfToken = token
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  }

  if (!(options?.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  if (csrfToken && options?.method && options.method !== 'GET') {
    headers['X-CSRF-Token'] = csrfToken
  }

  const res = await fetch(`${API}/${endpoint}`, {
    credentials: 'include',
    ...options,
    headers,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new ApiError(res.status, body.error || res.statusText)
  }

  return res.json()
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<AuthResponse>('auth.php', { method: 'POST', body: JSON.stringify({ email, password }) }),

  me: () => request<AuthResponse>('auth.php'),

  logout: () => request<{ success: boolean }>('auth.php', { method: 'DELETE' }),

  // Articles
  getPublicArticles: (params: Record<string, string>) =>
    request<ArticlesResponse>(`articles.php?${new URLSearchParams(params)}`),

  getAdminArticles: (params: Record<string, string> = {}) =>
    request<ArticlesResponse>(`articles.php?${new URLSearchParams({ admin: '1', ...params })}`),

  getArticle: (id: number) =>
    request<{ article: Article }>(`articles.php?id=${id}`),

  getArticleBySlug: (slug: string) =>
    request<{ article: Article }>(`articles.php?slug=${encodeURIComponent(slug)}`),

  createArticle: (data: ArticleInput) =>
    request<{ id: number; slug: string }>('articles.php', { method: 'POST', body: JSON.stringify(data) }),

  updateArticle: (id: number, data: Partial<ArticleInput>) =>
    request<{ success: boolean }>(`articles.php?id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteArticle: (id: number) =>
    request<{ success: boolean }>(`articles.php?id=${id}`, { method: 'DELETE' }),

  // Users
  getUsers: () => request<{ users: User[] }>('users.php'),

  createUser: (data: UserInput) =>
    request<{ id: number }>('users.php', { method: 'POST', body: JSON.stringify(data) }),

  updateUser: (id: number, data: UserProfileInput) =>
    request<{ success: boolean; pending?: boolean }>(`users.php?id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  approveUserProfile: (id: number) =>
    request<{ success: boolean }>(`users.php?id=${id}&action=approve`, { method: 'PUT', body: JSON.stringify({}) }),

  rejectUserProfile: (id: number) =>
    request<{ success: boolean }>(`users.php?id=${id}&action=reject`, { method: 'PUT', body: JSON.stringify({}) }),

  deleteUser: (id: number) =>
    request<{ success: boolean }>(`users.php?id=${id}`, { method: 'DELETE' }),

  // Categories
  getMenuCategories: () =>
    request<{ categories: Category[] }>('categories.php?menu=1'),

  getCategories: () =>
    request<{ categories: Category[] }>('categories.php'),

  createCategory: (data: CategoryInput) =>
    request<{ id: number; slug: string }>('categories.php', { method: 'POST', body: JSON.stringify(data) }),

  updateCategory: (id: number, data: Partial<CategoryInput>) =>
    request<{ success: boolean }>(`categories.php?id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteCategory: (id: number) =>
    request<{ success: boolean }>(`categories.php?id=${id}`, { method: 'DELETE' }),

  // Media
  getMedia: () => request<{ media: MediaItem[] }>('media.php'),

  uploadMedia: async (file: File): Promise<{ id: number; url: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    return request('media.php', { method: 'POST', body: formData })
  },

  deleteMedia: (id: number) =>
    request<{ success: boolean }>(`media.php?id=${id}`, { method: 'DELETE' }),

  // Videos
  uploadVideo: async (file: File): Promise<{ url: string; original_name: string; size: number }> => {
    const formData = new FormData()
    formData.append('file', file)
    return request('videos.php', { method: 'POST', body: formData })
  },
}
